import { render, unmountComponentAtNode } from "react-dom";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

import { BalconyGroup, BalconyInfo, PropertyCollectingInfo, propertyInfo } from "./balconyInfo";

interface PropProps {
    id: string;
    label: string;
    value: string;
    type: string;
    updateProperty: (id: string, value: string) => void;
    updateSelectedProps: (propId: string, selected: boolean) => void;
}

const BalconyProp = (props: PropProps) => {
    const [value, setValue] = useState(props.value);
    const [pressed, setPressed] = useState(false);

    let pattern = "";
    switch (props.type) {
        case "integer":
            pattern = "\\b(?:0|[1-9][0-9]*)\\b"
            break;
        case "double":
            pattern = "[0-9]+([,][0-9]*)?"
            break;
        default:
    }

    return (
        <div className="row">
            <div className="form-group col-sm-3">
                <label htmlFor={props.id} className="col-form-label">{props.label}</label>
            </div>
            <div className="form-group col-sm-8 mb-1">
                {(props.type === "integer" || props.type === "double") &&
                    <input type="text" pattern={pattern} className="form-control" id={props.id} value={value}
                        onChange={evt => {
                            if (evt.target.checkValidity())
                                setValue(evt.target.value);
                            else
                                evt.target.value = value;
                        }}
                        onBlur={evt => props.updateProperty(props.id, value)} />
                }
                {/*TODO: Boolean type --> switch or checkbox*/}
                {(props.type === "string" || props.type === "boolean") &&
                    <input type="text" className="form-control" id={props.id} value={value}
                        onChange={evt => setValue(evt.target.value)}
                        onBlur={evt => props.updateProperty(props.id, value)} />
                }
                {props.type === "text" &&
                    <textarea className="form-control" id={props.id} rows={3} value={value}
                        onChange={evt => setValue(evt.target.value)}
                        onBlur={evt => props.updateProperty(props.id, value)} />
                }
            </div>

            <div className="form-group col-sm-1">
                <button id="no-outline-button" type="button"
                    className={`form-control btn ${pressed ? "btn-secondary" : "btn-light"} float-right`}
                    style={{ width: "38px" }}
                    data-toggle="button" aria-pressed={pressed} tabIndex={-1}
                    onClick={async (e) => {
                        e.preventDefault();
                        setPressed((prev) => !prev);
                        await props.updateSelectedProps(props.id, !pressed);
                    }}>
                    {/*{pressed && <i className="ion-md-checkbox-outline" />}*/}
                    {/*{!pressed && <i className="ion-md-square-outline" />}*/}
                    {pressed && <i className="ion-md-checkmark-circle-outline" />}
                    {!pressed && <i className="ion-md-radio-button-off" />}

                </button>
            </div>
        </div>
    );
}

interface BalconyPropertiesProps {
    viewer: Autodesk.Viewing.GuiViewer3D;
    balcony: BalconyGroup;
    propertyInfo: PropertyCollectingInfo[];
    updateProperties: (balcony: BalconyGroup) => void;
    findGroupsByProperties: (propId: string, propValue: string) => Promise<BalconyGroup[]>;
    selectBalcony: (id: number, ignorePropsPanel: boolean) => Promise<void>;
}

function BalconyPropertiesUi(props: BalconyPropertiesProps) {
    const models = useRef<Autodesk.Viewing.Model[]>([]);

    const [selectedProps, setSelectedProps] = useState<string[]>([]);

    let properties = { ...props.balcony.propertyValues };

    const updateProperty = (id: string, value: string) => {
        let propInfo = props.propertyInfo.find(p => p.id === id);
        value = value.trim();
        if (value.length > 0) {
            value = props.balcony.formatValue(propInfo.propertyDefinition.name, value);
        }

        properties[propInfo.propertyDefinition.name] = value;
    }

    const updateSelectedProps = async (propId: string, selected: boolean) => {
        if (selected)
            setSelectedProps([...selectedProps, propId]);
        else
            setSelectedProps(selectedProps.filter(p => p !== propId));
    }

    const balconyHighlight = (group: BalconyGroup) => {
        var modelNum = 0;
        const elementDbIds = group.connectingIds;
        if (group.manual && group.panels.length > 0)
            modelNum = group.panels[0].modelId - 1;
        else
            modelNum = 0;

        for (let ed of elementDbIds) {
            models.current[modelNum].setThemingColor(ed, new THREE.Vector4(255, 234, 0, 255).toRgba(), true);
        }
    }

    const clearHighlight = () => {
        for (const model of models.current) {
            props.viewer.clearThemingColors(model);
        }
    }

    useEffect(() => {
        clearHighlight();

        // Try to find groups with mathching properties
        const findGroups = async () => {
            if (selectedProps.length > 0) {
                let propInfo = props.propertyInfo.find(p => p.id === selectedProps[0]);
                let groups = await props.findGroupsByProperties(propInfo.id, properties[propInfo.propertyDefinition.name]);

                // If more than one prop selected, go through them
                if (selectedProps.length > 1) {
                    for (let i = 1; i < selectedProps.length; i++) {
                        groups = groups.filter(g => {
                            propInfo = props.propertyInfo.find(prop => selectedProps[i] === prop.id);
                            if (propInfo) {
                                let name = propInfo.propertyDefinition.name;
                                return g.propertyValues[name] === properties[propInfo.propertyDefinition.name];
                            }
                        });
                    }
                }

                for (let group of groups) {
                    balconyHighlight(group);
                }

                props.viewer.impl.invalidate(true);
            }
            else {
                await props.selectBalcony(props.balcony.id, true);
            }
        }

        findGroups();

    }, [selectedProps]);

    useEffect(() => {
        models.current = props.viewer.getAllModels();

        return () => {
            clearHighlight();
            props.selectBalcony(props.balcony.id, true);
        }
    }, []);

    return (
        <div id="balcony-props-react" className="react-content m-2">
            <div style={{ height: "calc(100% - 60px)", overflowX: "hidden", overflowY: "scroll" }}>
                <form className="mr-1">
                    {/*<div className="form-group row">*/}
                    {props.propertyInfo.sort((a, b) => { return a.reportInfo.order - b.reportInfo.order }).map(p => {
                            return <BalconyProp key={p.id} id={p.id} label={p.reportInfo.reportName}
                                value={props.balcony.propertyValues[p.propertyDefinition.name] ?? ""}
                                type={p.propertyDefinition.type}
                                updateProperty={updateProperty}
                                updateSelectedProps={updateSelectedProps}
                                />;
                        })}
                    {/*</div>*/}
                </form>
            </div>
            <div className="row">
                <div className="col mt-2">
                    <button className="btn btn-primary float-right"
                        onClick={(e) => {
                            e.preventDefault();
                            for (let key in properties) {
                                if (properties[key].length == 0)
                                    delete properties[key];
                            }

                            props.balcony.propertyValues = properties;

                            props.updateProperties(props.balcony);
                        }}>
                        Update
                    </button>
                </div>
            </div>
            
        </div>
    );
}

class BalconyPropertiesPanel extends Autodesk.Viewing.UI.DockingPanel {

    private viewer: Autodesk.Viewing.GuiViewer3D;
    private balcony: BalconyGroup;

    private contentDiv: HTMLDivElement;
    private reactNode;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, container: any, id: string) {
        super(container, id, "Balcony Properties");
        this.viewer = viewer;

        this.container.classList.add("balcony-properties-panel");
        
        this.contentDiv = document.createElement("div");
        this.contentDiv.classList.add("content");
        this.container.appendChild(this.contentDiv);
    }

    setVisible(show: boolean, balcony: BalconyGroup = null, propertyInfoList: PropertyCollectingInfo[] = null,
        updateProperties: (balcony: BalconyGroup) => void = null,
        findGroupsByProperties: (propId: string, propValue: string) => any = null,
        selectBalcony: (id: number) => any = null) {
        super.setVisible(show);
        if (show) {
            unmountComponentAtNode(this.contentDiv);
            render(<BalconyPropertiesUi viewer={this.viewer} balcony={balcony} propertyInfo={propertyInfoList}
                updateProperties={updateProperties} findGroupsByProperties={findGroupsByProperties}
                selectBalcony={selectBalcony} />, this.contentDiv);
        } else if (this.contentDiv) {
            unmountComponentAtNode(this.contentDiv);
            this.reactNode = null;
        }
    }
}

export { BalconyPropertiesPanel };